import { Key, Plus, Prohibit } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { type ApiToken, type CreatedToken, tokensQuery } from "@/components/admin/api";
import { adminError } from "@/components/admin/errors";
import { plural, QueryBoundary } from "@/components/admin/shared";
import { CreateTokenDialog, SecretDialog } from "@/components/settings/TokenDialogs";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  Button,
  ConfirmDialog,
  type DataColumn,
  DataTable,
  EmptyState,
  IconButton,
  RelativeTime,
  Switch,
  TableSkeleton,
} from "@/components/ui";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/settings/tokens")({ component: TokensPage });

type TokenState = "active" | "revoked" | "expired";
const stateOf = (t: ApiToken): TokenState =>
  t.revokedAt ? "revoked" : t.expiresAt && Date.parse(t.expiresAt) <= Date.now() ? "expired" : "active";

function TokensPage() {
  useBreadcrumbs([{ label: m.nav_settings(), to: "/settings" }, { label: m.set_nav_tokens() }]);
  const q = useQuery(tokensQuery);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<CreatedToken | null>(null);

  return (
    <Page
      title={m.set_nav_tokens()}
      icon={Key}
      meta={m.tok_meta()}
      width="full"
      className="max-w-5xl"
      actions={
        <Button variant="primary" onPress={() => setCreating(true)}>
          <Plus size={14} aria-hidden="true" />
          {m.tok_create_title()}
        </Button>
      }
    >
      <QueryBoundary query={q} skeleton={<TableSkeleton rows={3} columns={4} />}>
        {(tokens) => <TokenTable tokens={tokens} onCreate={() => setCreating(true)} />}
      </QueryBoundary>
      <CreateTokenDialog isOpen={creating} onOpenChange={setCreating} onCreated={setSecret} />
      <SecretDialog token={secret} onClose={() => setSecret(null)} />
    </Page>
  );
}

function TokenTable({ tokens, onCreate }: { tokens: ApiToken[]; onCreate: () => void }) {
  const qc = useQueryClient();
  const [showRevoked, setShowRevoked] = useState(false);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);

  const rows = useMemo(
    () =>
      tokens
        .filter((t) => showRevoked || stateOf(t) === "active")
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
    [tokens, showRevoked],
  );
  const hidden = tokens.length - tokens.filter((t) => stateOf(t) === "active").length;

  const columns: DataColumn<ApiToken>[] = [
    {
      id: "name",
      header: m.field_name(),
      isRowHeader: true,
      className: "min-w-48",
      cell: (t) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate-title font-medium text-fg" title={t.name}>
            {t.name}
          </span>
          <span className="font-mono text-xs text-fg-muted">{t.prefix}…</span>
        </div>
      ),
    },
    {
      id: "access",
      header: m.tok_col_access(),
      className: "hidden md:table-cell",
      cell: (t) => (
        <div className="flex min-w-0 flex-col">
          <span className="text-fg">
            {t.scopes ? plural(t.scopes.length, m.tok_scopes_n_one, m.tok_scopes_n) : m.tok_scopes_all()}
          </span>
          {t.userKind === "bot" ? (
            <span className="truncate-title text-xs text-fg-muted">
              {t.onBehalfOfName
                ? m.tok_for_bot_behalf({ bot: t.userName, person: t.onBehalfOfName })
                : m.tok_for_bot({ bot: t.userName })}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      id: "used",
      header: m.tok_col_used(),
      className: "hidden w-32 lg:table-cell",
      cell: (t) =>
        t.lastUsedAt ? (
          <RelativeTime value={t.lastUsedAt} className="text-fg-muted" />
        ) : (
          <span className="text-fg-subtle">{m.ui_never()}</span>
        ),
    },
    {
      id: "expires",
      header: m.tok_col_expires(),
      className: "hidden w-32 sm:table-cell",
      cell: (t) =>
        t.expiresAt ? (
          <RelativeTime value={t.expiresAt} className="text-fg-muted" />
        ) : (
          <span className="text-fg-subtle">{m.ui_never()}</span>
        ),
    },
    {
      id: "status",
      header: m.adm_users_filter_status(),
      className: "w-28",
      cell: (t) => {
        const s = stateOf(t);
        return s === "active" ? (
          <Badge tone="success">{m.adm_status_active()}</Badge>
        ) : s === "expired" ? (
          <Badge tone="warning">{m.adm_status_expired()}</Badge>
        ) : (
          <Badge>{m.adm_status_revoked()}</Badge>
        );
      },
    },
    {
      id: "actions",
      header: m.ui_actions(),
      hideHeader: true,
      align: "end",
      className: "w-12",
      cell: (t) =>
        stateOf(t) === "revoked" ? null : (
          <IconButton
            aria-label={m.tok_revoke_aria({ name: t.name })}
            icon={Prohibit}
            size="sm"
            onPress={() => setRevoking(t)}
          />
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {tokens.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="max-w-xl text-sm text-pretty text-fg-muted">{m.tok_intro()}</p>
          {hidden > 0 ? (
            <div className="ml-auto">
              <Switch isSelected={showRevoked} onChange={setShowRevoked}>
                {m.tok_show_inactive({ count: hidden })}
              </Switch>
            </div>
          ) : null}
        </div>
      ) : null}
      <DataTable
        label={m.set_nav_tokens()}
        columns={columns}
        rows={rows}
        renderEmptyState={() => (
          <EmptyState
            icon={Key}
            title={tokens.length === 0 ? m.tok_empty_title() : m.tok_none_title()}
            description={tokens.length === 0 ? m.tok_empty_body() : m.tok_none_body()}
            action={
              <Button variant="primary" onPress={onCreate}>
                {m.tok_create_title()}
              </Button>
            }
            secondary={
              <Link
                to="/settings/claude"
                className="rounded-control px-2 text-sm text-accent-text underline underline-offset-2"
              >
                {m.tok_empty_claude()}
              </Link>
            }
          />
        )}
      />
      <ConfirmDialog
        isOpen={revoking !== null}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={m.tok_revoke_title({ name: revoking?.name ?? "" })}
        description={m.tok_revoke_body()}
        confirmLabel={m.ui_revoke()}
        onConfirm={async () => {
          if (!revoking) return;
          await api.delete(`/api/v1/tokens/${revoking.id}`);
          await qc.invalidateQueries({ queryKey: tokensQuery.queryKey });
          toast.show({ title: m.tok_revoked(), tone: "success" });
        }}
        describeError={adminError}
      />
    </div>
  );
}
